import type { BlogArticle } from '@/lib/blog-data'
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count'

export const post: BlogArticle = {
    slug: 'can-you-change-tesla-horn-sound',
    title: 'Can You Change Tesla Horn Sound?',
    excerpt: "We found no way to change the Tesla horn sound. The lock sound is customizable. Here's what you can and can't change, with the regulations and recall reports behind it.",
  image: '/images/blog/generated/can-you-change-tesla-horn-sound.png',
    author: 'Tesla Lock Sound Team',
    publishedAt: '2026-04-16',
    category: 'tips-tricks',
    tags: ['tesla horn', 'customization', 'lock sound', 'faq', 'boombox'],
    readingTime: 6,
    soundCategory: 'trending',
    soundLimit: 4,
    featured: false,
    content: `
# Can You Change Your Tesla Horn Sound?

The short answer: **we found no way to change the Tesla horn sound**, and no Tesla documentation that offers one. Check your owner's manual for your car.

But the sound most people actually want to customize (the chirp when you lock your car) **is** customizable on cars with the external speaker. If you landed here because you want your Tesla to play something more interesting when you walk away from it, you're in the right place.

This page also explains the regulations people usually mean when they ask this. It describes what the regulations say, with links, and is not legal advice. Rules differ by country and state, so check your local law.

---

## Why You Can't Change the Tesla Horn

Tesla does not offer a setting for it, and we have not found a Tesla statement explaining why. What we can point to is that horns are covered by vehicle regulations in many places:

- **UN Regulation No. 28** covers audible warning devices and the audible signals of motor vehicles in countries that apply UN/ECE regulations. For vehicle categories M and N, it sets the vehicle's audible signal between **93 and 112 dB(A)**, measured **7 m in front of the vehicle** (paragraphs 14.4 and 14.8). Read the [regulation text on EUR-Lex](https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:42011X1206%2802%29).
- **FMVSS No. 141 is not a horn standard.** It is titled "Minimum Sound Requirements for Hybrid and Electric Vehicles" and covers the pedestrian alert sound that electric and hybrid vehicles make at low speed. People often mix the two up. Read it in the [eCFR, 49 CFR 571.141](https://www.ecfr.gov/current/title-49/subtitle-B/chapter-V/part-571/subpart-B/section-571.141).

Whether a particular change to a horn is legal where you live is a question for local law, not this page.

## What FMVSS 141 Does Say About Vehicle Sounds

49 CFR 571.141 applies to electric and hybrid vehicles with a gross vehicle weight rating of 4,536 kg or less (S3). It requires a pedestrian alert sound when the vehicle is stationary, in reverse, and at low speeds up to about 30 km/h (S5.1), and sets minimum sound levels in A-weighted dB by frequency band. Its S8 says that no entity subject to NHTSA's authority may "disable, alter, replace, or modify any element of a vehicle installed as original equipment for purposes of complying with this Standard", except for repair of a malfunction or to remedy a defect or non-compliance.

That is about the pedestrian alert sound system. The lock sound is a different feature, covered below.

## The 2022 Boombox Recalls

Boombox plays sounds through an external speaker. In 2022 NHTSA recall reports said that this could obscure the car's Pedestrian Warning System (PWS) sounds:

- **Recall 22V-063** (received 4 February 2022): Tesla recalled 578,607 vehicles (2020-2022 Model S, X and Y, and 2017-2022 Model 3). The report says the Boombox function "allows sounds to be played through an external speaker while the vehicle is in motion," which "may obscure the Pedestrian Warning System (PWS) sounds" and means the vehicles fail to comply with FMVSS 141. The remedy was an over-the-air update disabling Boombox "when the vehicle is in Drive, Neutral and Reverse modes." [NHTSA campaign 22V-063](https://www.nhtsa.gov/recalls?nhtsaId=22V063).
- **Recall 22V-235** (received 12 April 2022): superseded 22V-063 for 594,894 vehicles of the same models and years, with the remedy extended to include Summon and Smart Summon. [NHTSA campaign 22V-235](https://www.nhtsa.gov/recalls?nhtsaId=22V235).

So the Boombox restriction is Tesla's remedy for those recall reports. Your lock sound plays when you lock the car, which is a different use from playing a sound while the car is moving.

## What You CAN Customize on Your Tesla

### Lock Sound (the one you probably want)

This is the chirp that plays when you lock your car. It is **customizable** on cars with the external speaker (Model 3 built from September 2019, Model S and X from the 2021 refresh, every Model Y and Cybertruck) on software 2023.44 or later.

- Works on **Model 3, Model Y, Model S, Model X, and Cybertruck**
- Load a WAV file named exactly \`LockChime.wav\` into a \`Boombox\` folder on a USB drive
- Plug the USB into a USB port that carries data (the glovebox port on newer cars)
- This is what most people mean when they say "I want to change my Tesla's sound"

There are ${PUBLIC_SOUND_COUNT_LABEL} sounds you can use, everything from movie quotes to video game sound effects to dramatic bass drops.

[Browse ${PUBLIC_SOUND_COUNT_LABEL} free lock sounds →](/sounds)

### Boombox Mode

Boombox lets Tesla play sounds through the external speaker.

- Built-in sound options, plus custom sounds from USB
- Since the 2022 recalls above, it is disabled in Drive, Neutral and Reverse, including Summon and Smart Summon

Boombox is fun, but it's not a horn replacement.

## Horn vs. Lock Sound: Quick Comparison

| Feature | Horn | Lock Sound |
|---------|------|------------|
| Customizable? | No setting found | Yes, with a USB file |
| Trigger | Steering wheel | Locking the car |
| File format | N/A | WAV named \`LockChime.wav\` (44.1 kHz, 16-bit recommended) |

## How to Install a Custom Lock Sound

1. **Download a sound** from the [sound library](/sounds): every file is a \`LockChime.wav\`
2. **Create a folder** named \`Boombox\` on a FAT32 or exFAT USB drive and put the file inside, so the path is \`Boombox/LockChime.wav\`
3. **Plug the USB** into a USB port that carries data (the glovebox port on newer cars)
4. **Select it** under Toybox → Boombox → Lock Sound, then choose USB

[Full step-by-step guide →](/guide)

## Popular "Horn Replacement" Lock Sounds

These are the lock sounds people install when they wanted a custom horn:

- **Air horn blasts**: the closest you'll get to a horn-style sound
- **Train horn**
- **Foghorn**
- **Stadium horn**
- **Loud alarm chirp**

All of these play as your lock sound, not through the horn.

[Browse all sounds →](/sounds)

## FAQ

### Did Tesla ever allow custom horn sounds?

We found no Tesla documentation of horn customization in any software version. The confusion usually comes from Boombox, which plays custom sounds through an external speaker.

### Can you change the Tesla horn to a train horn?

We found no way to. What you *can* do is install a train horn sound as your [lock sound](/sounds), so you hear it every time you lock the car.

### Is the Tesla Boombox the same as the horn?

Boombox is a separate feature that plays sounds through an external speaker. NHTSA's recall reports (linked above) connect it to the Pedestrian Warning System sounds, not to the horn.

### What's the loudest Tesla lock sound?

Lock sounds play through the external speaker. Air horns, train horns, and stadium horns from our [sound library](/sounds) are the loudest-sounding options, but the level a speaker can reach depends on Tesla's hardware, not the file.

### Does changing the lock sound void my Tesla warranty?

Custom lock sounds were added in Tesla's 2023 Holiday Update (software 2023.44), so it is a feature Tesla ships. We can't give warranty advice; check Tesla's warranty terms or ask Tesla Service. See our [warranty post](/blog/does-changing-tesla-lock-sound-void-warranty).

---

Want to go deeper? Learn [what a Tesla lock chime actually is](/lockchime) under the hood, or check out the [best Tesla lock sounds](/blog/best-tesla-lock-sounds).
`
  }
